import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  blockUserRequest,
  unblockUserRequest,
  resetUserPasswordRequest,
  clearSuccessMessage,
} from '_admin/features/users/usersSlice';
import {
  selectActionStatus,
  selectActionErrors,
  selectSuccessMessage,
} from '_admin/features/users/usersSelectors';

export const useUserActions = () => {
  const dispatch = useDispatch();

  const actionStatus = useSelector(selectActionStatus);
  const actionErrors = useSelector(selectActionErrors);
  const successMessage = useSelector(selectSuccessMessage);

  const [confirmation, setConfirmation] = useState(null);

  const showBlockConfirm = useCallback((user) => {
    setConfirmation({ type: 'block', user });
  }, []);

  const showUnblockConfirm = useCallback((user) => {
    setConfirmation({ type: 'unblock', user });
  }, []);

  const showResetPasswordConfirm = useCallback((user) => {
    setConfirmation({ type: 'resetPassword', user });
  }, []);

  const dismissConfirmation = useCallback(() => {
    setConfirmation(null);
  }, []);

  const executeAction = useCallback(() => {
    if (!confirmation) return;

    const { type, user } = confirmation;

    switch (type) {
      case 'block':
        dispatch(blockUserRequest({ userId: user.id }));
        break;
      case 'unblock':
        dispatch(unblockUserRequest({ userId: user.id }));
        break;
      case 'resetPassword':
        dispatch(resetUserPasswordRequest({ userId: user.id }));
        break;
      default:
        break;
    }

    setConfirmation(null);
  }, [confirmation, dispatch]);

  const blockUser = useCallback((userId) => {
    dispatch(blockUserRequest({ userId }));
  }, [dispatch]);

  const unblockUser = useCallback((userId) => {
    dispatch(unblockUserRequest({ userId }));
  }, [dispatch]);

  const resetPassword = useCallback((userId) => {
    dispatch(resetUserPasswordRequest({ userId }));
  }, [dispatch]);

  const dismissSuccessMessage = useCallback(() => {
    dispatch(clearSuccessMessage());
  }, [dispatch]);

  return {
    blocking: actionStatus.blocking,
    unblocking: actionStatus.unblocking,
    resettingPassword: actionStatus.resettingPassword,
    isAnyActionInProgress: actionStatus.blocking || actionStatus.unblocking || actionStatus.resettingPassword,

    blockError: actionErrors.block,
    unblockError: actionErrors.unblock,
    resetPasswordError: actionErrors.resetPassword,

    successMessage,
    dismissSuccessMessage,

    confirmation,
    showBlockConfirm,
    showUnblockConfirm,
    showResetPasswordConfirm,
    dismissConfirmation,
    executeAction,

    blockUser,
    unblockUser,
    resetPassword,
  };
};
